import ServiceNameWithPromos from "../components/ServiceNameWithPromos";
import {track} from '../analytics/service';
import { Shirt } from "lucide-react";
// src/pages/Servizi.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getFornitoreCompleto, listAddressOptions } from "../Admin/adminApi";
import SupplierAddressDialog from "../components/profile/SupplierAddressDialog";
import { supplierAddressCompatibility } from "../utils/supplierAddressCompatibility";
import StarRating from "../components/StarRating";
import SupplierImage from "../components/SupplierImage";
import ReviewsDrawer from "../components/ReviewsDrawer";
import SupplierHoursButton from "../components/SupplierHoursButton";
import useIsMobile from "../hooks/isMobile";
import { useCart } from "../context/CartContext";
import toast from "react-hot-toast";
import {
  SERVICE_CATEGORIES,
  getDbIdFromKey,
  getKeyFromDbId,
} from "../config/serviceCategories";
import "./css/servizi.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ---- helpers immagini (assolutizza URL + scelta campo migliore) ----
const absolutize = (u) => {
  if (!u) return null;
  const s = String(u).trim();
  if (!s) return null;
  if (/^(https?:)?\/\//i.test(s) || s.startsWith("data:")) return s; // già assoluto
  const base = (API_BASE || "").replace(/\/$/, "");
  const path = s.startsWith("/") ? s : `/${s}`;
  return base ? `${base}${path}` : s;
};

const pickCoverUrl = (data, stateCover) => {
  const cands = [
    stateCover,
    data?.fornitore?.cover_url,
    data?.cover_url,
    data?.fornitore?.logo_url,
    data?.logo_url,
    data?.fornitore?.coverUrl,
    data?.coverUrl,
    data?.fornitore?.logoUrl,
    data?.logoUrl,
  ];
  const hit = cands.find((x) => x && String(x).trim());
  return absolutize(hit) || "/images/ciao_stiro.png";
};

export default function Servizi() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const nomeFromState = location.state?.nome || "";
  const defaultCategoria = location.state?.categoria ?? null; // 1=stiro,2=lav,3=entrambi,null=tutti
  const token = localStorage.getItem("token");
  const isMobile = useIsMobile();

  const {
    items,
    totals,
    addItem,
    bumpQty,
    changeQty,
    removeItem,
    setLogisticsFee,
    openCart,
    setCartMeta,
    cartMeta,
  } = useCart();
  const cartSupplierId = items[0]?.id_fornitore;

  // ===== UI / Stato =====
  const [loading, setLoading] = useState(true);
  const [fornitoreNome, setFornitoreNome] = useState(nomeFromState);
  const [servizi, setServizi] = useState([]);
  const [fasceOrarie, setFasceOrarie] = useState([]);
  const [openHours, setOpenHours] = useState(false);

  // ⭐ Recensioni
  const [openReviews, setOpenReviews] = useState(false);
  const [rating, setRating] = useState({ avg: 0, count: 0 });

  // categoria selezionata: null = tutti, altrimenti "lavaggio"/"stiratura"/"entrambi"
  const [catSelezionata, setCatSelezionata] = useState(() => {
    const num = Number(defaultCategoria);
    if (num === 1 || num === 2 || num === 3) {
      return getKeyFromDbId(num) || null;
    }
    return null;
  });

  const [deliveryFee, setDeliveryFee] = useState(0);
  const [coverUrl, setCoverUrl] = useState(
    () => absolutize(location.state?.cover_url) || null,
  );

  // Indirizzo
  const queryAddress = new URLSearchParams(location.search).get("address");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressCompatible, setAddressCompatible] = useState(false);
  const [addressChecking, setAddressChecking] = useState(true);
  const [addressOptions, setAddressOptions] = useState([]);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressContext, setAddressContext] = useState(null);
  const activeAddressObjFromState = selectedAddress;

  const addressStringFromState = location.state?.address || null;
  const organizationContextFromState =
    addressContext || location.state?.organizationContext || null;
  const persistedAddressKey = Object.prototype.hasOwnProperty.call(location.state || {}, "selectedAddress")
    ? null : `${cartMeta?.address_selection?.source_type || ""}:${cartMeta?.address_selection?.source_id || ""}`;

  useEffect(() => {
    let alive = true;
    setAddressCompatible(false);
    setAddressChecking(true);
    setSelectedAddress(null);
    setAddressContext(null);
    setAddress("");
    (async () => {
      try {
        const options = await listAddressOptions(token);
        if (!alive) return;
        setAddressOptions(options);
        const explicit = Object.prototype.hasOwnProperty.call(location.state || {}, "selectedAddress");
        const selection = location.state?.selectedAddress || location.state?.activeAddress ||
          (!explicit && (!items.length || Number(items[0].id_fornitore) === Number(id)) ? cartMeta?.address_selection : null);
        const chosen = selection
          ? options.find(a => a.source_type === selection.source_type && Number(a.source_id) === Number(selection.source_id))
          : !explicit ? options.find(a => a.source_type === "user" && a.active) : null;
        if (chosen) {
          const result = await supplierAddressCompatibility(chosen, id, token);
          if (alive && result.compatible) { setAddressContext(result.context || null); setSelectedAddress(chosen); setAddressCompatible(true); }
        }
      } catch { if (alive) setAddressOptions([]); }
      finally { if (alive) setAddressChecking(false); }
    })();
    return () => { alive = false; };
    // Route changes revalidate the saved selection, including after refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, location.key, persistedAddressKey]);

  const selectSupplierAddress = (chosen, context) => {
    // Browsing another supplier must never overwrite the existing cart's
    // address. The existing replacement confirmation still owns that decision.
    setShowAddressDialog(false);
    setActiveQtyFor(null);
    navigate({ pathname: location.pathname, search: location.search }, { replace: true,
      state: { ...location.state, address: null, selectedAddress: chosen, organizationContext: context } });
  };

  const [myReview, setMyReview] = useState(null);
  const cartFeedbackTimer = useRef(null);

  const showCartAddedFeedback = () => {
    clearTimeout(cartFeedbackTimer.current);
    toast.success("Aggiunto al carrello", { id: "services-cart-added", duration: 2200 });
    // Also expire while the toaster is paused by hover/touch on a mobile browser.
    cartFeedbackTimer.current = setTimeout(() => toast.dismiss("services-cart-added"), 2200);
  };

  useEffect(() => () => {
    clearTimeout(cartFeedbackTimer.current);
    toast.dismiss("services-cart-added");
  }, [id]);

  const formatActiveAddress = (a) => {
    if (!a) return "";
    const left = a.label || a.via || a.indirizzo || a.street || "";
    const right = [a.citta, a.provincia && `(${a.provincia})`]
      .filter(Boolean)
      .join(" ");
    return [left, right].filter(Boolean).join(" · ");
  };

  const stateAddressFormatted =
    activeAddressObjFromState ? addressStringFromState || formatActiveAddress(activeAddressObjFromState) : "";

  const [address, setAddress] = useState(
    stateAddressFormatted ||
      "",
  );

  const [activeQtyFor, setActiveQtyFor] = useState(null);

  useEffect(() => {
    if (!activeAddressObjFromState || !addressCompatible ||
      (items.length && Number(cartSupplierId) !== Number(id))) return;

    const label =
      activeAddressObjFromState.display_name ||
      activeAddressObjFromState.label ||
      formatActiveAddress(activeAddressObjFromState) ||
      "";

    const isOrganizationAddress =
      String(activeAddressObjFromState.source_type || "").toLowerCase() ===
      "organization";

    setCartMeta((prev) => ({
      ...prev,
      ...(prev.address_selection?.source_type === activeAddressObjFromState.source_type &&
        Number(prev.address_selection?.source_id) === Number(activeAddressObjFromState.source_id)
        ? {} : { ritiro_at: null, consegna_at: null, ritiro_slot: null, consegna_slot: null }),
      indirizzo: label,
      address_selection:
        activeAddressObjFromState.source_type &&
        activeAddressObjFromState.source_id != null
          ? {
              source_type: String(activeAddressObjFromState.source_type),
              source_id: Number(activeAddressObjFromState.source_id),
              label,
            }
          : null,
      organization_context: isOrganizationAddress
        ? {
            organization_id:
              Number(organizationContextFromState?.organization?.id) || null,
            supplier_id:
              Number(organizationContextFromState?.supplier?.id) || Number(id),
          }
        : null,
    }));
  }, [
    activeAddressObjFromState,
    organizationContextFromState,
    id,
    setCartMeta,
    addressCompatible,
    items.length,
    cartSupplierId,
  ]);

  useEffect(() => {
    if (stateAddressFormatted) {
      localStorage.setItem("alc_address", stateAddressFormatted);
      setAddress(stateAddressFormatted);
    } else if (addressCompatible && queryAddress) {
      localStorage.setItem("alc_address", queryAddress);
      setAddress(queryAddress);
    }
  }, [stateAddressFormatted, queryAddress, addressCompatible]);

  const fmtPrice = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "€ —";
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(n);
  };

  // FETCH "me" (ha il token)
  useEffect(() => {
    if (!token || !API_BASE) {
      setMyReview(null);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/fornitori/${id}/reviews/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) {
          setMyReview(null);
          return;
        }
        const js = await r.json(); // null o {id, rating, comment, created_at}
        setMyReview(js);
      } catch {
        setMyReview(null);
      }
    })();
  }, [id, token]);

  // ===== Fetch dati fornitore + servizi =====
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await getFornitoreCompleto(id, token);

        const nomeApi = data?.nome_fornitore || data?.nome || "";
        if (!fornitoreNome && nomeApi) setFornitoreNome(nomeApi);

        const rawServizi =
          (Array.isArray(data?.servizi) && data.servizi) ||
          (Array.isArray(data?.fornitore?.servizi) && data.fornitore.servizi) ||
          [];

        // cover/logo con normalizzazione URL
        setCoverUrl(pickCoverUrl(data, location.state?.cover_url));

        const serviziNorm = rawServizi.map((s) => {
          const descrizione = s.descrizione ?? s.descrizione_capo ?? "Servizio";
          const descrizione_estesa_raw =
            typeof s.descrizione_estesa === "string"
              ? s.descrizione_estesa.trim()
              : "";

          return {
            ...s, // prima tutto l'originale
            id_servizio: s.id_servizio ?? s.id,
            descrizione,
            descrizione_estesa: descrizione_estesa_raw || null, // vuota -> null
            prezzo: s.prezzo,
            tipo_id: s.id_categoria_servizio, // 1=stiro 2=lav 3=entrambi
          };
        });
        setServizi(serviziNorm);
        track('supplier_catalog_viewed',{screen:'catalog',result_count:serviziNorm.length},`catalog:${id}:${location.key}`);

        const fee =
          Number(
            data?.fornitore?.logistics_fee ?? data?.logistics_fee ?? 2.0,
          ) || 0;
        setDeliveryFee(fee);

        setFasceOrarie(
          Array.isArray(data?.fasce_orarie) ? data.fasce_orarie : [],
        );
      } catch (e) {
        console.error(e);
        toast.error("Impossibile caricare i servizi del fornitore.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    if (!loading && (!items.length || Number(cartSupplierId) === Number(id))) setLogisticsFee(deliveryFee);
  }, [loading, deliveryFee, items.length, cartSupplierId, id, setLogisticsFee]);

  // ===== Rating (summary) =====
  useEffect(() => {
    if (!API_BASE) return;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/fornitori/${id}/reviews/summary`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (r.ok) {
          const js = await r.json();
          setRating({ avg: js.avg || 0, count: js.count || 0 });
        }
      } catch {
        // ignore
      }
    })();
  }, [id, token]);

  // SOLO su Servizi (mobile): flag su <body> per override CSS del FAB carrello
  useEffect(() => {
    if (isMobile) {
      document.body.classList.add("alc-servizi");
      return () => document.body.classList.remove("alc-servizi");
    }
    // desktop: assicurati che la classe non resti appiccicata
    document.body.classList.remove("alc-servizi");
    return undefined;
  }, [isMobile]);

  // ===== Derivati =====
  const serviziFiltrati = useMemo(() => {
    if (!Array.isArray(servizi)) return [];
    if (!catSelezionata) return servizi;
    const dbId = getDbIdFromKey(catSelezionata);
    if (!dbId) return servizi;
    return servizi.filter((s) => Number(s.tipo_id) === Number(dbId));
  }, [servizi, catSelezionata]);

  const orariAggregati = useMemo(
    () => (Array.isArray(fasceOrarie) ? fasceOrarie : []),
    [fasceOrarie],
  );

  // Presenza per ogni categoria (lavaggio/stiratura/entrambi) in base ai servizi del fornitore
  const categoryPresence = useMemo(() => {
    const present = { lavaggio: false, stiratura: false, entrambi: false };
    if (Array.isArray(servizi)) {
      servizi.forEach((s) => {
        const t = Number(s.tipo_id);
        const key = getKeyFromDbId(t);
        if (key && Object.prototype.hasOwnProperty.call(present, key)) {
          present[key] = true;
        }
      });
    }
    return present;
  }, [servizi]);

  // Se la categoria selezionata non è più disponibile, facciamo fallback
  useEffect(() => {
    if (loading) return;
    if (!Array.isArray(servizi) || servizi.length === 0) return;
    if (!catSelezionata) return; // "Tutti" è sempre valido

    if (!categoryPresence[catSelezionata]) {
      const order = ["lavaggio", "stiratura", "entrambi"];
      const firstExisting = order.find((k) => categoryPresence[k]);
      setCatSelezionata(firstExisting ?? null);
    }
  }, [loading, servizi, categoryPresence, catSelezionata]);

  // ===== Helpers carrello =====
  const thisSupplierId = Number(id);

  const cartItemIdFor = (sid) => `svc-${sid}-${thisSupplierId}`;

  const resolveCartItemId = (sid) => {
    const idA = `svc-${sid}-${thisSupplierId}`;
    const idB = `svc-${sid}`; // legacy
    const match =
      items.find((i) => i.id === idA) ||
      items.find(
        (i) => i.id === idB && Number(i.id_fornitore) === thisSupplierId,
      ) ||
      items.find(
        (i) =>
          Number(i.id_servizio) === Number(sid) &&
          Number(i.id_fornitore) === thisSupplierId,
      );
    return match?.id || idA;
  };

  const getCartQty = (sid) => {
    const it =
      items.find(
        (i) =>
          Number(i.id_servizio) === Number(sid) &&
          Number(i.id_fornitore) === thisSupplierId,
      ) ||
      items.find((i) => {
        const idA = `svc-${sid}-${thisSupplierId}`;
        const idB = `svc-${sid}`; // legacy
        return (
          i.id === idA ||
          (i.id === idB && Number(i.id_fornitore) === thisSupplierId)
        );
      });
    return Math.max(0, Number(it?.qty || 0));
  };

  // Cambi fornitore → chiudi eventuali stepper “appesi”
  useEffect(() => {
    setActiveQtyFor(null);
  }, [id]);

  const ensureAddItem = (s, qtyToAdd = 1) => {
    track('add_to_cart_attempt',{catalog_id:Number(s.id_servizio)});
    if (!addressCompatible) { track('address_blocked',{error_code:'address_required'});setShowAddressDialog(true); return false; }
    const baseItem = {
      id: cartItemIdFor(s.id_servizio),
      id_servizio: s.id_servizio,
      id_fornitore: thisSupplierId,
      descrizione_capo: s.descrizione,
      descrizione: s.descrizione,
      fornitore_nome: fornitoreNome || "",
      prezzo: parseFloat(s.prezzo),
      qty: Math.max(1, qtyToAdd),
    };

    const res = addItem(baseItem, { allowReplace: false });
    if (!res?.ok && res?.reason === "DIFFERENT_SUPPLIER") {
      toast(
        (t) => (
          <span>
            Hai già articoli di un altro fornitore.
            <br />
            Vuoi svuotare il carrello e aggiungere questo articolo?
            <div className="toast-actions">
              <button
                onClick={() => toast.dismiss(t.id)}
                className="btn btn-ghost btn-sm"
              >
                Annulla
              </button>
              <button
                onClick={() => {
                  const r2 = addItem(baseItem, { allowReplace: true });
                  toast.dismiss(t.id);
                  if (!r2?.ok) toast.error("Aggiornamento carrello fallito");
                  else showCartAddedFeedback();
                }}
                className="btn btn-primary btn-sm"
              >
                Conferma
              </button>
            </div>
          </span>
        ),
        { duration: Infinity },
      );

      return false;
    }
    if (!res?.ok) {
      toast.error("Errore nell’aggiunta al carrello.");
      return false;
    }
    showCartAddedFeedback();
    return true;
  };

  const onPlus = (s) => {
    if (!addressCompatible) { track('add_to_cart_attempt',{catalog_id:Number(s.id_servizio)});track('address_blocked',{error_code:'address_required'});setShowAddressDialog(true); return; }
    const cur = getCartQty(s.id_servizio);
    const cartId = resolveCartItemId(s.id_servizio);
    if (cur > 0) {
      track('add_to_cart_attempt',{catalog_id:Number(s.id_servizio)});
      bumpQty(cartId, +1);
      showCartAddedFeedback();
      setActiveQtyFor(s.id_servizio);
    } else {
      const ok = ensureAddItem(s, 1);
      if (ok) setActiveQtyFor(s.id_servizio);
    }
  };

  const onMinus = (s) => {
    const cur = getCartQty(s.id_servizio);
    const cartId = resolveCartItemId(s.id_servizio);
    if (cur <= 0) return;
    if (cur === 1) {
      removeItem(cartId);
      setActiveQtyFor((prev) => (prev === s.id_servizio ? null : prev));
    } else {
      bumpQty(cartId, -1);
    }
  };

  const onDirectQty = (s, nextVal) => {
    const v = Math.max(0, Number(nextVal) || 0);
    const cur = getCartQty(s.id_servizio);
    if (v > cur && !addressCompatible) { track('add_to_cart_attempt',{catalog_id:Number(s.id_servizio)});track('address_blocked',{error_code:'address_required'});setShowAddressDialog(true); return; }
    const cartId = resolveCartItemId(s.id_servizio);
    if (v === 0) {
      if (cur > 0) removeItem(cartId);
      setActiveQtyFor((prev) => (prev === s.id_servizio ? null : prev));
      return;
    }
    if (cur === 0) {
      const ok = ensureAddItem(s, v);
      if (ok) setActiveQtyFor(s.id_servizio);
    } else {
      if (v>cur) track('add_to_cart_attempt',{catalog_id:Number(s.id_servizio)});
      changeQty(cartId, v);
    }
  };

  const totalQty = useMemo(
    () => items.reduce((acc, it) => acc + Math.max(0, Number(it.qty || 0)), 0),
    [items],
  );

  useEffect(() => {
    document.body.classList.toggle("alc-has-cartbar", totalQty > 0);
    return () => document.body.classList.remove("alc-has-cartbar");
  }, [totalQty]);

  useEffect(() => {
    const root = document.documentElement;

    const selectEl = () => document.querySelector(".sv-cart-bar");
    const updateH = () => {
      const el = selectEl();
      const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
      root.style.setProperty("--sv-cart-bar-h", h ? `${h}px` : "68px");
    };

    updateH();

    const el = selectEl();
    let ro;
    if (el && "ResizeObserver" in window) {
      ro = new ResizeObserver(updateH);
      ro.observe(el);
    }

    const onResize = () => updateH();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [totalQty]);

  const displayTotalNoLogistics = useMemo(() => {
    const gross =
      Number(totals?.total ?? 0) || Number(totals?.totalCents ?? 0) / 100;
    const fee = Number(totals?.fee ?? 0) || Number(totals?.feeCents ?? 0) / 100;
    return Math.max(0, gross - fee);
  }, [totals]);

  // ===== Render =====
  return (
    <div className={`sv-page sv-page-root ${totalQty > 0 ? "has-cart" : ""}`}>
      {/* Header */}
      <header className="sv-header">
        <div className="sv-header-row">
          <button
            className="sv-back"
            onClick={() => navigate(-1)}
            aria-label="Indietro"
          >
            <span>‹</span>
          </button>
          <div className="sv-header-title">Dettaglio fornitore</div>
        </div>

        {/* Indirizzo selezionato */}
        <div className="sv-rcbar">
          <div className="sv-addr-chip" title={address}>
            {address || "Nessun indirizzo selezionato"}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="sv-hero">
        <SupplierImage
          src={coverUrl || "/images/ciao_stiro.png"}
          alt={`Copertina ${fornitoreNome || `Fornitore #${id}`}`}
          onError={() => setCoverUrl("/images/ciao_stiro.png")}
          style={{ objectFit: "cover" }}
        />
        <div className="sv-hero-body">
          <div className="sv-supplier-name">
            {fornitoreNome || `Fornitore #${id}`}
          </div>

          <div className="sv-meta">
            <StarRating
              value={Number(rating.avg) || 0}
              count={Number(rating.count) || 0}
              onClick={() => setOpenReviews(true)}
              size={18}
            />

            {myReview && (
              <span
                className="sv-myreview-chip"
                title={`Hai già recensito: ${myReview.rating}/5`}
              >
                La tua: <strong>{Number(myReview.rating) || 0}/5</strong>
              </span>
            )}

            <span>•</span>

            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 12h18" />
            </svg>
            <span>Consegna {fmtPrice(deliveryFee)}</span>

            <SupplierHoursButton
              orari={orariAggregati}
              label={fornitoreNome || `Fornitore #${id}`}
              isOpen={openHours}
              onToggle={() => setOpenHours((p) => !p)}
              onClose={() => setOpenHours(false)}
            />
          </div>
        </div>
      </section>

      {/* Filtro categorie */}
      <div className="sv-segmented" role="tablist" aria-label="Filtro servizi">
        <button
          className={`sv-seg-btn ${!catSelezionata ? "active" : ""}`}
          onClick={() => setCatSelezionata(null)}
          aria-pressed={!catSelezionata}
        >
          Tutti
        </button>

        {SERVICE_CATEGORIES.map((c) => {
          const shouldShow = loading || categoryPresence[c.key];
          if (!shouldShow) return null;
          return (
            <button
              key={c.key}
              className={`sv-seg-btn ${
                catSelezionata === c.key ? "active" : ""
              }`}
              onClick={() => setCatSelezionata(c.key)}
              aria-pressed={catSelezionata === c.key}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Lista servizi */}
      <div className="sv-list">
        {loading ? (
          <div className="sv-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="sv-skel-row" key={i}>
                <span className="sv-skel-line w-60" />
                <span className="sv-skel-line w-20" />
                <span className="sv-skel-btn" />
              </div>
            ))}
          </div>
        ) : !Array.isArray(serviziFiltrati) || serviziFiltrati.length === 0 ? (
          <div className="sv-empty">
            <div className="sv-empty-illust">🧴</div>
            <h3>Nessun servizio disponibile</h3>
            <p className="sv-hint">
              Questo fornitore non ha servizi in questa categoria.
            </p>
          </div>
        ) : (
          serviziFiltrati.map((s) => {
            const q = getCartQty(s.id_servizio);
            const stepperOpen = q > 0 || activeQtyFor === s.id_servizio;
            return (
              <div className="sv-item" key={s.id_servizio}>
                <span className="sv-item-thumb" aria-hidden="true">
                  <Shirt size={24} />
                  {(s.immagine || s.image || s.image_url || s.img || s.custom_image) && (
                    <img
                      src={absolutize(s.immagine || s.image || s.image_url || s.img || s.custom_image)}
                      alt=""
                      loading="lazy"
                      onError={event => { event.currentTarget.style.visibility = "hidden"; }}
                    />
                  )}
                </span>
                <div className="sv-item-name-wrap">
                  <ServiceNameWithPromos name={s.descrizione} className="sv-item-name" promos={s.active_quantity_promos} />
                  {s.descrizione_estesa && (
                    <div className="sv-item-sub">{s.descrizione_estesa}</div>
                  )}
                </div>
                <div className="sv-item-price">{fmtPrice(s.prezzo)}</div>

                {stepperOpen ? (
                  <div
                    className={`sv-stepper ${stepperOpen ? "is-open" : ""}`}
                    role="group"
                    aria-label={`Quantità per ${s.descrizione}`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <button
                      className={`sv-stepper-icon ${
                        q === 0 ? "is-disabled" : ""
                      }`}
                      aria-label="Diminuisci"
                      onClick={() => onMinus(s)}
                      disabled={q === 0}
                    >
                      −
                    </button>

                    <input
                      className="sv-stepper-val"
                      type="number"
                      min={0}
                      value={q}
                      onChange={(e) => onDirectQty(s, e.target.value)}
                      aria-label="Quantità"
                    />

                    <button
                      className="sv-stepper-icon"
                      aria-label="Aumenta"
                      disabled={addressChecking}
                      onClick={() => onPlus(s)}
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <button
                    className="sv-plus-chip"
                    aria-label="Aggiungi"
                    disabled={addressChecking}
                    title="Aggiungi"
                    onClick={() => onPlus(s)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <span aria-hidden="true">+</span>
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Drawer recensioni */}
      {showAddressDialog && <SupplierAddressDialog supplierId={id} token={token} options={addressOptions}
        onClose={() => {track('address_flow_result',{outcome:'cancelled'});setShowAddressDialog(false);}} onSelect={selectSupplierAddress} />}
      <ReviewsDrawer
        open={openReviews}
        onClose={() => setOpenReviews(false)}
        fornitoreId={id}
        fornitoreNome={fornitoreNome || `Fornitore #${id}`}
        onSaved={setMyReview}
      />

      {/* FAB recensioni: solo desktop */}
      {!isMobile && Number(rating.count) > 0 && (
        <button
          className="sv-reviews-fab"
          onClick={() => setOpenReviews(true)}
          title="Vedi recensioni"
        >
          ★
        </button>
      )}

      {/* Cart bottom bar */}
      {totalQty > 0 && (
        <div
          className="sv-cart-bar"
          role="region"
          aria-label="Riepilogo carrello"
        >
          <div className="sv-cart-badge" aria-live="polite">
            {totalQty}
          </div>
          <div className="sv-cart-info">
            <div className="sv-cart-line-1">
              {totalQty} {totalQty === 1 ? "capo" : "capi"} nel carrello
            </div>
            <div className="sv-cart-line-2">
              Totale {fmtPrice(displayTotalNoLogistics)}
            </div>
          </div>
          <button
            className="sv-cart-btn"
            onClick={openCart}
            title="Vai al Carrello"
          >
            Vai al Carrello
          </button>
        </div>
      )}
    </div>
  );
}
